import { useMutation, useQuery } from "@apollo/client";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { blueGrey } from "@mui/material/colors";
import { GenericCommentStream } from "components/Comments/GenericCommentStream";
import { useAlerts } from "components/Alerts/AlertProvider";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { gql } from "generated-graphql";
import { CommentThreadFragment } from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useThemeMode } from "hooks/useThemeMode";
import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { prettyPrintDateMed } from "util/dates";

const WASTE_SUBMISSION_DETAIL_QUERY = gql(`
  query WasteSubmissionDetailForStaffLanding($submissionId: ID!) {
    wasteSubmissionDetail(submissionId: $submissionId) {
      tenantName
      facilityName
      state
      epaIds
      ownerName
      ownerEmail
      staffReviewer {
        id
        email
        firstName
        lastName
        isSigner
      }
      submission {
        id
        tenantId
        title
        dueDate
        status
        submissionType
      }
      internalComments {
        id
        reportId
        userId
        workflowId
        activityId
        submissionId
        comment
        isSystemMessage
        createdAt
        user {
          id
          email
          person {
            first
            last
          }
        }
      }
    }
  }
`);

const ADD_SUBMISSION_INTERNAL_COMMENT_MUTATION = gql(`
  mutation AddSubmissionInternalCommentForStaffLanding(
    $submissionId: ID!
    $comment: String!
  ) {
    addSubmissionInternalComment(submissionId: $submissionId, comment: $comment) {
      id
    }
  }
`);

const DELETE_SUBMISSION_INTERNAL_COMMENT_MUTATION = gql(`
  mutation DeleteSubmissionInternalCommentForStaffLanding($id: ID!) {
    deleteSubmissionInternalComment(id: $id)
  }
`);

type WasteSubmissionDetailData = {
  wasteSubmissionDetail: {
    tenantName: string;
    facilityName: string | null;
    state: string | null;
    epaIds: string[];
    ownerName: string | null;
    ownerEmail: string | null;
    staffReviewer: {
      id: string;
      email: string | null;
      firstName: string | null;
      lastName: string | null;
      isSigner: boolean;
    } | null;
    submission: {
      id: string;
      tenantId: string;
      title: string | null;
      dueDate: string | null;
      status: string;
      submissionType: string;
    };
    internalComments: CommentThreadFragment[];
  };
};

type WasteSubmissionDetailVariables = {
  submissionId: string;
};

type AddSubmissionInternalCommentVariables = {
  submissionId: string;
  comment: string;
};

type DeleteSubmissionInternalCommentVariables = {
  id: string;
};

function DetailField({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <Stack direction="row" spacing={2} alignItems="baseline">
      <Typography
        variant="caption"
        sx={{
          minWidth: "140px",
          color: "text.secondary",
          fontWeight: 500,
          letterSpacing: 0.2,
        }}
      >
        {label}
      </Typography>
      <Typography variant="body2">{value || "—"}</Typography>
    </Stack>
  );
}

export function WasteSubmissionLandingPage() {
  const theme = useTheme();
  const { themeMode } = useThemeMode();
  const { submissionId } = useParams();
  const { user } = useCurrentUser();
  const alerts = useAlerts();

  useBreadcrumb([
    { label: "Fulfillment", to: "/staff/fulfillment/waste" },
    { label: "Waste Submission" },
  ]);

  const { data, loading, error, refetch } = useQuery<
    WasteSubmissionDetailData,
    WasteSubmissionDetailVariables
  >(WASTE_SUBMISSION_DETAIL_QUERY, {
    variables: { submissionId: submissionId ?? "" },
    skip: !submissionId,
    fetchPolicy: "network-only",
  });

  const [addComment, { loading: addingComment }] = useMutation<
    unknown,
    AddSubmissionInternalCommentVariables
  >(ADD_SUBMISSION_INTERNAL_COMMENT_MUTATION);

  const [deleteComment, { loading: deletingComment }] = useMutation<
    unknown,
    DeleteSubmissionInternalCommentVariables
  >(DELETE_SUBMISSION_INTERNAL_COMMENT_MUTATION);

  const submission = data?.wasteSubmissionDetail.submission;
  const staffReviewerText = useMemo(() => {
    const reviewer = data?.wasteSubmissionDetail.staffReviewer;
    if (!reviewer) {
      return "—";
    }
    const name =
      reviewer.firstName || reviewer.lastName
        ? `${reviewer.firstName ?? ""} ${reviewer.lastName ?? ""}`.trim()
        : (reviewer.email ?? "—");
    return reviewer.isSigner ? `${name} (Sign)` : name;
  }, [data?.wasteSubmissionDetail.staffReviewer]);

  const customerSubmissionEditPath = useMemo(() => {
    if (!submission?.tenantId || !submission?.id) return null;
    return `/o/${submission.tenantId}/waste/submissions/${submission.id}/edit`;
  }, [submission?.id, submission?.tenantId]);

  const commentThreadData = useMemo(() => {
    return data?.wasteSubmissionDetail.internalComments ?? [];
  }, [data?.wasteSubmissionDetail.internalComments]);

  const onAddComment = async (message: string) => {
    if (!submissionId || !message.trim()) return;
    try {
      await addComment({
        variables: {
          submissionId,
          comment: message.trim(),
        },
      });
      await refetch();
    } catch (mutationError) {
      console.error(mutationError);
      alerts.error("Failed to add internal comment.");
    }
  };

  const onDeleteComment = async (commentId: string) => {
    try {
      await deleteComment({ variables: { id: commentId } });
      await refetch();
    } catch (mutationError) {
      console.error(mutationError);
      alerts.error("Failed to delete internal comment.");
    }
  };

  if (!submissionId) {
    return (
      <Alert severity="error">
        No submission id provided. Go back to the waste fulfillment list.
      </Alert>
    );
  }

  if (loading) {
    return (
      <Stack
        p={3}
        sx={{
          background: blueGrey[themeMode === "light" ? 50 : 900],
          minHeight: "calc(100vh - 180px)",
        }}
      >
        <Paper elevation={0} sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Loading waste submission...
          </Typography>
        </Paper>
      </Stack>
    );
  }

  if (error || !data?.wasteSubmissionDetail || !submission) {
    return (
      <Alert severity="error">
        Failed to load waste submission detail.
      </Alert>
    );
  }

  return (
    <Stack
      p={3}
      spacing={2}
      sx={{
        background: blueGrey[themeMode === "light" ? 50 : 900],
        minHeight: "calc(100vh - 180px)",
      }}
    >
      <Paper elevation={0} sx={{ p: theme.spacing(3) }}>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={1.5}
          >
            <Stack spacing={0.5}>
              <Typography variant="h6">
                {submission.title || "Waste Submission"}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  size="small"
                  label={prettyPrintEnumValue(submission.status)}
                  sx={{
                    backgroundColor: theme.palette.action.selected,
                    fontWeight: 500,
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  {prettyPrintEnumValue(submission.submissionType)}
                </Typography>
              </Stack>
            </Stack>

            <Stack direction="row" spacing={1}>
              <Button component={Link} to="/staff/fulfillment/waste" variant="text">
                Back to List
              </Button>
              {customerSubmissionEditPath ? (
                <Button
                  component={Link}
                  to={customerSubmissionEditPath}
                  target="_blank"
                  variant="outlined"
                  endIcon={<OpenInNewIcon />}
                >
                  Open Customer Edit
                </Button>
              ) : null}
            </Stack>
          </Stack>
        </Stack>
      </Paper>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "5fr 7fr" },
          gap: 2,
        }}
      >
        <Paper elevation={0} sx={{ p: theme.spacing(3), height: "100%" }}>
          <Stack spacing={2}>
            <Typography variant="h6">Submission Details</Typography>
            <DetailField label="Organization" value={data.wasteSubmissionDetail.tenantName} />
            <DetailField
              label="Facility"
              value={data.wasteSubmissionDetail.facilityName || "—"}
            />
            <DetailField label="State" value={data.wasteSubmissionDetail.state || "—"} />
            <DetailField
              label="EPA IDs"
              value={
                data.wasteSubmissionDetail.epaIds.length > 0
                  ? data.wasteSubmissionDetail.epaIds.join(", ")
                  : "—"
              }
            />
            <DetailField
              label="Due Date"
              value={submission.dueDate ? prettyPrintDateMed(submission.dueDate) : "—"}
            />
            <DetailField
              label="Encamp Assignee"
              value={
                data.wasteSubmissionDetail.ownerName ||
                data.wasteSubmissionDetail.ownerEmail ||
                "—"
              }
            />
            <DetailField
              label="Peer Reviewer"
              value={staffReviewerText}
            />
          </Stack>
        </Paper>

        <Paper elevation={0} sx={{ p: theme.spacing(3), height: "100%" }}>
          <GenericCommentStream
            title="Internal Comments"
            comments={commentThreadData}
            currentUserId={user?.id}
            onAddComment={onAddComment}
            onDeleteComment={onDeleteComment}
            addLoading={addingComment}
            deleteLoading={deletingComment}
            placeholder="Leave an internal comment"
            buttonText="Comment"
            emptyStateMessage="No internal comments yet."
            showDeleteButton
          />
        </Paper>
      </Box>
    </Stack>
  );
}
